import { useEffect, useRef } from "react";
import { isSnapshot } from "@/lib/seo";

/**
 * Lightweight scroll reveal — CSS transition + IntersectionObserver, no
 * framer-motion. Same API as before (delay, className). The `.reveal` base
 * state is styled in index.css; `.is-visible` is toggled via the DOM (not
 * React state) to keep hydration deterministic. A <noscript> style in
 * index.html reveals content when JS is unavailable.
 */
export function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (isSnapshot()) return;
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("is-visible");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { el.classList.add("is-visible"); io.disconnect(); } }),
      { rootMargin: "0px 0px -70px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={["reveal", className].filter(Boolean).join(" ")} style={delay ? { transitionDelay: `${delay}s` } : undefined}>
      {children}
    </div>
  );
}

export function SectionHead({ eyebrow, title, sub, center }) {
  return (
    <Reveal className={`max-w-[62ch] mb-14${center ? " mx-auto text-center" : ""}`}>
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="text-[clamp(1.85rem,3.4vw,2.5rem)] mt-3 mb-3.5">{title}</h2>
      {sub && <p className={`text-muted text-[1.06rem] max-w-[56ch]${center ? " mx-auto" : ""}`}>{sub}</p>}
    </Reveal>
  );
}
